'use client'

import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Controller, useForm, useWatch, FormProvider } from 'react-hook-form';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import dayjs from 'dayjs';
import _ from 'lodash';
import AsyncSelectCreatable from 'react-select/async-creatable';
import Select from 'react-select';
import { QualificationNode, UniversityNode } from 'generated/types';

import {
  ControlComponent,
  ICreatableNewOption,
  MenuComponent,
  OptionComponent,
  PlaceholderComponent
} from 'components/select';
import { AccordionContent, AccordionItem, AccordionTrigger } from 'components/ui/accordion';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';

import { DateType, yearsRange } from 'utils/date';
import {
  useCreateQualificationMutation, useCreateUniversityMutation,
  useLoadQualificationsMutation, useLoadUniversitiesMutation,
  useUpdateEducationMutation
} from 'generated/hooks';
import { z } from 'zod';
import { useFormErrorHandling } from 'hooks/useFormErrorHandling';
import { useToast } from 'hooks/useToast';
import { LoaderCircle } from 'lucide-react';
import { useTransientButton } from 'app/_components/useTransientButton';
import { UpdateEducationMutation } from 'generated/operations';

export type EducationNodeFromBe = Omit<NonNullable<UpdateEducationMutation['updateEducation']>['education'], '__typename'>;

export interface IEducationForm {
  id: string | null;
  university: Omit<UniversityNode, '__typename'>;
  qualification: Omit<QualificationNode, '__typename'>;
  fromMonth: DateType | null;
  fromYear: DateType;
  toMonth: DateType | null;
  toYear:  DateType | null;
  isCurrentlyStudyHere: boolean,
  localId?: number;
}

interface IExperienceCard {
  qualifications: QualificationNode[];
  universities: UniversityNode[];
  educationItem: IEducationForm & { localId?: number };
  index: number;
  isLastCard: boolean;
  isOpen: boolean;
  onUpdateItem: (education: IEducationForm, educationFromBE: EducationNodeFromBe) => void;
  onDelete: (education: IEducationForm) => void;
  onSetDirtyCard: (id: string, isDirty: boolean) => void;
}

const educationSchema = z.object({
  id: z.string().nullable(),
  university: z.object({
    id: z.string(),
    name: z.string()
  }, { message: 'This field is required' }),
  qualification: z.object({
    id: z.string(),
    name: z.string()
  }, { message: 'This field is required' }),

  fromYear: z.object({
    title: z.string(),
    value: z.number()
  }, { message: 'This field is required' }).required({ value: true }),
  fromMonth: z.object({
    title: z.string(),
    value: z.number()
  }, { message: 'This field is required' }).nullable(),
  toMonth: z.object({
    title: z.string(),
    value: z.number()
  }, { message: 'This field is required' }).nullable(),
  toYear: z.object({
    title: z.string(),
    value: z.number()
  }, { message: 'This field is required' }).nullable(),
  isCurrentlyStudyHere: z.boolean(),
  localId: z.number().optional().nullable(),
}).superRefine((({ isCurrentlyStudyHere, toYear, fromYear }, ctx)  => {
  if (!isCurrentlyStudyHere && (!toYear || !toYear.value)) {
    ctx.addIssue({
      path: ['toYear'],
      message: 'Year is required if you do not currently study here',
      code: "custom"
    });
  }

  if (toYear && fromYear) {
    const fromDate = dayjs(`${fromYear.value}-01-01`);
    const toDate = dayjs(`${toYear.value}-01-01`);

    if (toDate.isBefore(fromDate)) {
      ctx.addIssue({
        path: ['toYear'],
        message: 'End date cannot be earlier than start date',
        code: "custom"
      });
    }
  }
}));

export const EducationCard: FC<IExperienceCard> = (props) => {
  const {
    qualifications,
    universities,
    educationItem,
    index,
    isLastCard,
    isOpen,
    onUpdateItem,
    onDelete,
    onSetDirtyCard,
  } = props;

  const [educationItemSaved] = useState<IEducationForm>({ ...educationItem });
  const [qualificationsSaved, setSavedQualifications] = useState(qualifications);
  const [universitiesSaved, setSavedUniversities] = useState(universities);

  const [, loadQualifications] = useLoadQualificationsMutation();
  const [, loadUniversities] = useLoadUniversitiesMutation()
  const [, createQualification] = useCreateQualificationMutation();
  const [, createUniversity] = useCreateUniversityMutation();
  const [updateEducationState, updateEducation] = useUpdateEducationMutation();

  const { handleErrors } = useFormErrorHandling();
  const { handleToast } = useToast();

  const { transientBtn, transientShow } = useTransientButton({
    initial: 'Save',
    transient: 'Saved'
  });

  const form = useForm({
    mode: 'all',
    values: {
      ...educationItemSaved,
    },
    resolver: standardSchemaResolver(educationSchema)
  });

  const {
    control,
    handleSubmit,
    formState: { errors: isValid },
    setError,
    setValue,
    clearErrors
  } = form;

  const getQualificationOptions = useCallback((qualification?: QualificationNode | null) => {
    if (!qualification) {
      return qualificationsSaved;
    }

    return qualificationsSaved.findIndex(item => item.id === qualification.id) > -1 ? qualificationsSaved : [qualification, ...qualificationsSaved];
  }, [qualificationsSaved]);

  const getUniversityOptions = useCallback((university?: UniversityNode | null) => {
    if (!university) {
      return universitiesSaved;
    }

    return universitiesSaved.findIndex(item => item.id === university.id) > -1 ? universitiesSaved : [university, ...universitiesSaved];
  }, [universitiesSaved]);

  const handleSaveEducation = useCallback(async (education: IEducationForm) => {
    try {
      const response = await updateEducation({
        description: '',
        toYear: education.toYear ? education.toYear.value : null,
        fromMonth: education.fromMonth?.value ? education.fromMonth.value + 1 : 1,
        fromYear: education.fromYear!.value,
        toMonth: education.toMonth ? (education.toMonth.value + 1) : null,
        id: education.id ?? null,
        qualificationId: education.qualification?.id ?? '',
        universityId: education.university?.id ?? ''
      });

      if (response.error) {
        handleToast('Oops! Something went wrong!', 'error');
        return handleErrors(response?.error, setError);
      }

      if (!response.data?.updateEducation) {
        handleToast('Oops! Something went wrong!', 'error');
        return;
      }

      if (!!education.localId) {
        onSetDirtyCard(education.localId.toString(), false);
      } else if (!!education.id) {
        onSetDirtyCard(education.id, false);
      }

      onUpdateItem({
        ...education,
        id: response.data?.updateEducation?.education.id ?? '',
      }, response.data?.updateEducation.education);

      transientShow();
    } catch (error) {
      console.error(error);
    }
  }, [handleErrors, handleToast, onSetDirtyCard, onUpdateItem, setError, transientShow, updateEducation]);

  const onSubmit = useCallback(async (data: any): Promise<unknown> => {
    try {
      if (!data.university?.id) {
        const response = await createUniversity({
          name: data.university.name,
        })

        if (response.error) {
          console.error(response.error);

          return ;
        }

        return onSubmit({
          ...data,
          university: response.data?.createUniversity?.university,
        });
      }

      if (!data.qualification?.id) {
        const response = await createQualification({
          name: data.qualification.name,
        })

        if (response.error) {
          console.error(response.error);

          return ;
        }

        return onSubmit({
          ...data,
          qualification: response.data?.createQualification?.qualification,
        });
      }

      return handleSaveEducation(data);
    } catch (error) {
      console.error(error);
    }
  }, [createQualification, createUniversity, handleSaveEducation]);

  const formRef = useRef<HTMLFormElement>(null);

  const educationWatch = useWatch({
    control
  });

  const checkIsDirty = useCallback((value1: any, value2: any) => {
    return !_.isEqual(value1, value2);
  }, []);

  const isDirty = useMemo(() => checkIsDirty(educationItem, educationWatch), [checkIsDirty, educationItem, educationWatch]);

  const handleDelete = useCallback(async () => {
    onDelete(educationWatch as IEducationForm);
  }, [educationWatch, onDelete]);

  useEffect(() => {
    if (educationItem.id) {
      onSetDirtyCard(educationItem.id, isDirty);
    } else if (!!educationItem.localId) {
      onSetDirtyCard(educationItem.localId.toString(), true);
    }
  }, [educationItem.id, educationItem.localId, index, isDirty, isValid, onSetDirtyCard]);

  return (
    <div className="flex flex-col gap-10">
      {!!index && (
        <div className="border-b border-b-gray-200" />
      )}
      <AccordionItem
        value={(index + 1).toString()}
        className={`rounded-radius-4xl p-8 ${!isLastCard ? 'mb-10' : ''} ${isOpen ? 'shadow-3xl bg-white border-b-0' : '!bg-gray-25 border border-gray-200'}`}
        key={educationItem.id || educationItem.localId || index}
      >
        <AccordionTrigger className="!p-0 w-full gap-2">
          <p className="text-[30px]/[38px] text-left truncate font-medium">
            {educationWatch.qualification?.name ?? ''}{educationWatch.qualification?.name && educationWatch.university?.name ? ',' : ''} {educationWatch.university?.name}
          </p>
        </AccordionTrigger>
        <AccordionContent>
          <FormProvider {...form}>
            <form
              className="flex flex-col gap-6 pt-2"
              ref={isLastCard ? formRef : undefined}
              onSubmit={handleSubmit(onSubmit)}
            >
              <Controller
                render={({ field, formState: { errors } }) => (
                  <div className="flex flex-col gap-sm flex-1">
                    <label
                      className={`font-medium text-small max-w-max flex text-gray-900 relative`}
                      htmlFor={`education-select-${index}`}
                    >
                      Education
                    </label>
                    <AsyncSelectCreatable
                      inputId={`education-select-${index}`}
                      isClearable
                      cacheOptions
                      isSearchable
                      className="w-full"
                      menuPortalTarget={document.body}
                      defaultOptions={getUniversityOptions(educationItem.university)}
                      getOptionLabel={(university: UniversityNode) => university?.name || (university as unknown as ICreatableNewOption)?.label || ''}
                      getOptionValue={(university: UniversityNode) => university.id}
                      createOptionPosition="first"
                      onCreateOption={(inputValue) => {
                        const newUniversityItem: UniversityNode = {
                          id: '',
                          name: inputValue,
                          logo: null,
                        };

                        setSavedUniversities(prevState => [...prevState, newUniversityItem]);
                        setValue('university', newUniversityItem, { shouldDirty: true, shouldValidate: true });
                      }}
                      loadOptions={(inputValue, callback) => {
                        try {
                          loadUniversities({
                            search: inputValue
                          }).then((response) => {
                            callback(response.data?.loadUniversities?.universities ?? []);
                          }).catch(error => {
                            console.error(error);
                            callback([]);
                          });
                        } catch (error) {
                          console.error(error);
                        }
                      }}
                      {...field}
                      components={{
                        Control: ControlComponent,
                        Option: OptionComponent,
                        Menu: MenuComponent,
                        Placeholder: PlaceholderComponent
                      }}
                      error={errors.university?.message}
                    />
                    {errors.university &&
                      <p className={`text-small text-error-600`}>{errors.university?.message}</p>
                    }
                  </div>
                )}
                name={`university`}
                control={control}
              />
              <Controller
                render={({ field, formState: { errors } }) => (
                  <div className="flex flex-col gap-sm flex-1">
                    <label
                      className={`font-medium text-small max-w-max flex text-gray-900 relative`}
                      htmlFor={`degree-select-${index}`}
                    >
                      Degree
                    </label>
                    <AsyncSelectCreatable
                      inputId={`degree-select-${index}`}
                      isClearable
                      cacheOptions
                      isSearchable
                      className="w-full"
                      menuPortalTarget={document.body}
                      defaultOptions={getQualificationOptions(educationItem.qualification)}
                      getOptionLabel={(qualification: QualificationNode) => qualification?.name || (qualification as unknown as ICreatableNewOption)?.label || ''}
                      getOptionValue={(qualification: QualificationNode) => qualification.id}
                      onCreateOption={(inputValue) => {
                        const newQualificationItem: QualificationNode = {
                          id: '',
                          name: inputValue,
                        };

                        setSavedQualifications(prevState => [...prevState, newQualificationItem]);
                        setValue('qualification', newQualificationItem, { shouldDirty: true, shouldValidate: true });
                      }}
                      loadOptions={(inputValue, callback) => {
                        try {
                          loadQualifications({
                            search: inputValue
                          }).then((response) => {
                            callback(response.data?.loadQualifications?.qualifications ?? []);
                          }).catch((error) => {
                            console.error(error)

                            callback([]);
                          });

                        } catch (error) {
                          console.error(error);
                        }
                      }}
                      {...field}
                      components={{
                        Control: ControlComponent,
                        Option: OptionComponent,
                        Menu: MenuComponent,
                        Placeholder: PlaceholderComponent
                      }}
                      error={errors.qualification?.message}
                    />
                    {errors.qualification &&
                      <p className={`text-small text-error-600`}>{errors.qualification?.message}</p>
                    }
                  </div>
                )}
                name={`qualification`}
                control={control}
              />
              <div className="flex flex-col gap-15">
                <div className="flex gap-15">
                  <Controller
                    control={control}
                    name={`fromYear`}
                    render={({ field: { value, ...field }, formState: { errors } }) => (
                      <div className="flex flex-col gap-sm flex-1">
                        <label
                          className={`font-medium text-small max-w-max flex text-gray-900 relative`}
                          htmlFor={`from-year-select-${index}`}
                        >
                          Year Started
                        </label>
                        <Select
                          value={value}
                          placeholder="Select year"
                          inputId={`from-year-select-${index}`}
                          isClearable
                          isSearchable
                          className="w-full"
                          options={yearsRange()}
                          getOptionLabel={(year: DateType) => year.title}
                          getOptionValue={(year: DateType) => year.value.toString()}
                          {...field}
                          menuPortalTarget={document.body}
                          components={{
                            Control: ControlComponent,
                            Option: OptionComponent,
                            Menu: MenuComponent,
                            Placeholder: PlaceholderComponent
                          }}
                          error={errors.fromYear?.message}
                        />
                        {errors.fromYear &&
                          <p
                            className={`text-small text-error-600`}>{errors.fromYear?.message}</p>
                        }
                      </div>
                    )}
                  />
                  <div className="flex-1">
                    {!educationWatch.isCurrentlyStudyHere && (
                      <div className="flex gap-15">
                        <Controller
                          control={control}
                          name={`toYear`}
                          render={({ field: { value, ...field }, formState: { errors } }) => (
                            <div className="flex flex-col gap-sm flex-1">
                              <label
                                className={`font-medium text-small max-w-max flex text-gray-900 relative h-[20px]`}
                                htmlFor={`from-year-select-${index}`}
                              >Year Finished</label>
                              <Select
                                value={value}
                                placeholder="Select year"
                                inputId={`to-year-select-${index}`}
                                isClearable
                                isSearchable
                                className="w-full"
                                menuPortalTarget={document.body}
                                options={yearsRange()}
                                getOptionLabel={(year: DateType) => year.title}
                                getOptionValue={(year: DateType) => year.value.toString()}
                                {...field}
                                components={{
                                  Control: ControlComponent,
                                  Option: OptionComponent,
                                  Menu: MenuComponent,
                                  Placeholder: PlaceholderComponent
                                }}
                                error={errors.toYear?.message}
                              />
                              {errors.toYear &&
                                <p
                                  className={`text-small text-error-600`}>{errors.toYear?.message}</p>
                              }
                            </div>
                          )}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <Controller
                  control={control}
                  name="isCurrentlyStudyHere"
                  render={({ field: { value, onChange, ...field } }) => (
                    <label className="font-medium font-poppins text-gray-700 text-[14px]/[20px] flex items-center"
                           htmlFor={`currently_work_here_${index}`}>
                      <Checkbox
                        id={`currently_work_here_${index}`}
                        className="mr-2"
                        checked={value}
                        onCheckedChange={(checked) => {
                          clearErrors(['toMonth', 'toYear']);
                          onChange(checked);
                        }}
                        {...field}
                      />
                      I currently study here
                    </label>
                  )}
                />
              </div>
              <div className="flex justify-between items-center">
                <Button variant="nakedGrayLink" className="p-0" type="button" onClick={handleDelete}>
                  Delete Card
                </Button>
                <Button variant="primaryBrand" size="small" className="px-[60px] ml-auto gap-3"
                        onClick={handleSubmit(onSubmit)}>
                  {updateEducationState.fetching && (
                    <LoaderCircle className="animate-spin" />
                  )}
                  {transientBtn}
                </Button>
              </div>
            </form>
          </FormProvider>
        </AccordionContent>
      </AccordionItem>
    </div>
  );
};
